"use client";
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import type { Product, Station } from '../../lib/interface';
import { track } from '@/lib/tracking';

interface CompareItem {
  product: Product;
  station: Station;
}

interface CompareContextType {
  compareIds: string[];
  compareItems: CompareItem[];
  addToCompare: (product: Product, station: Station) => void;
  removeFromCompare: (id: string) => void;
  clearCompare: () => void;
  isInCompare: (id: string) => boolean;
  compareCount: number;
  refreshCompareItems: (currentProducts: Product[]) => void;
  getShareableUrl: () => string;
  getCompareDataForPage: () => { stationIds: string[]; stationNames: string[] };
  updateUrlForSharing: () => void;
}

const CompareContext = createContext<CompareContextType | undefined>(undefined);

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (context === undefined) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
};

interface CompareProviderProps {
  children: ReactNode;
}

export const CompareProvider: React.FC<CompareProviderProps> = ({ children }) => {
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [compareItems, setCompareItems] = useState<CompareItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Function to refresh compare items from current product data
  const refreshCompareItems = useCallback((currentProducts: Product[]) => {
    if (compareIds.length === 0) return;
    
    
    const validCompareItems = compareIds.map(bikeId => {
      // Find the product in current products list
      const product = currentProducts.find(p => 
        p.stations.some(s => s.bikeId === bikeId)
      );
      
      if (product) {
        const station = product.stations.find(s => s.bikeId === bikeId);
        if (station) {
          return { product, station };
        }
      }
      
      return null;
    }).filter(Boolean) as CompareItem[];
    
    
    // Only update if we found valid items
    if (validCompareItems.length > 0) {
      setCompareItems(validCompareItems);
    }
  }, [compareIds]);

  // Initialize from URL params on mount
  useEffect(() => {
    if (!isInitialized && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const compareParam = urlParams.get('compareIdsSet');
      const urlCompareIds = compareParam ? compareParam.split(',').filter(Boolean) : [];
      
      if (urlCompareIds.length > 0) {
        setCompareIds(urlCompareIds);
      }
      setIsInitialized(true);
    }
  }, [isInitialized]);


  const addToCompare = useCallback((product: Product, station: Station) => {
    if (!compareIds.includes(station.bikeId) && compareIds.length < 3) {
      const newCompareIds = [...compareIds, station.bikeId];
      const newCompareItems = [...compareItems, { product, station }];
      
      setCompareIds(newCompareIds);
      setCompareItems(newCompareItems);

      track('compare_list_updated', {
        action: 'added',
        vehicle_name: product.name,
        brand: product.brandName,
        price: station.price || product.minPrice,
        city: '',
      });
    }
  }, [compareIds, compareItems]);

  const removeFromCompare = useCallback((id: string) => {
    const removedItem = compareItems.find(item => item.station.bikeId === id);
    const newCompareIds = compareIds.filter(compareId => compareId !== id);
    const newCompareItems = compareItems.filter(item => item.station.bikeId !== id);
    
    setCompareIds(newCompareIds);
    setCompareItems(newCompareItems);

    if (removedItem) {
      track('compare_list_updated', {
        action: 'removed',
        vehicle_name: removedItem.product.name,
        city: '',
      });
    }
  }, [compareIds, compareItems]);

  const clearCompare = useCallback(() => {
    setCompareIds([]);
    setCompareItems([]);
    
    // No URL update during interactions (prevents re-renders)
  }, []);

  const isInCompare = useCallback((id: string) => {
    return compareIds.includes(id);
  }, [compareIds]);

  // Function to get shareable URL (doesn't update current URL, just returns a new one)
  const getShareableUrl = useCallback(() => {
    if (typeof window === 'undefined') return '';
    
    const url = new URL(window.location.href);
    if (compareIds.length > 0) {
      url.searchParams.set('compareIdsSet', compareIds.join(','));
    } else {
      url.searchParams.delete('compareIdsSet');
    }
    return url.toString();
  }, [compareIds]);

  // Function to manually update URL for sharing (called when user wants to share)
  const updateUrlForSharing = useCallback(() => {
    if (typeof window === 'undefined') return;
    
    const url = new URL(window.location.href);
    if (compareIds.length > 0) {
      url.searchParams.set('compareIdsSet', compareIds.join(','));
    } else {
      url.searchParams.delete('compareIdsSet');
    }
    
    // Update URL for sharing (this will cause a re-render, but it's intentional for sharing)
    window.history.pushState({}, '', url.toString());
  }, [compareIds]);

  // Function to get compare data for compare page
  const getCompareDataForPage = useCallback(() => {
    const stationIds = compareItems.map(item => item.station.stationId);
    const stationNames = compareItems.map(item => item.station.stationName);
    return { stationIds, stationNames };
  }, [compareItems]);

  const value: CompareContextType = {
    compareIds,
    compareItems,
    addToCompare,
    removeFromCompare,
    clearCompare,
    isInCompare,
    compareCount: compareIds.length,
    refreshCompareItems,
    getShareableUrl,
    getCompareDataForPage,
    updateUrlForSharing,
  };

  return (
    <CompareContext.Provider value={value}>
      {children}
    </CompareContext.Provider>
  );
}; 